/**
 * AlertBoardPage (shared) — the operator's alert board, in either of its two presentations,
 * behind a toggle in the toolbar.
 *
 * The honeycomb (every camera, clustered site → group) and the map (the cameras pinned on one
 * floor plan) were separate pages showing the SAME live data through the same `useAlertBoard`
 * engine, the same colour language and the same docked alert panel. Two sidebar entries for
 * one board meant an operator who wanted the spatial read of an alert had to leave the page
 * and lose their position; merging them makes it one click in place.
 *
 * Only the LAYOUT swaps. Which board is on screen is a presentation choice, not a different
 * surface, so the toggle is a segmented control in the CENTRE of each board's toolbar rather
 * than tabs — dead centre because it is the one control on the bar that belongs to neither
 * board, and the page's own name is not repeated there (the sidebar and title bar say it).
 *
 * The two boards are mounted exclusively, never both-with-one-hidden: each holds live WebRTC
 * subscriptions and its own realtime watch, and paying for both so the idle one can stay warm
 * would double every stream on the page.
 *
 * Node-agnostic like the boards it wraps: `alertsApi` / `mapApi` and the video player are
 * injected, so local passes SFU wiring and cloud passes its per-node tunnel wiring.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPinIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import AlertHexagonPage from './AlertHexagonPage';
import CameraMapPage from './CameraMapPage';
import SegmentedToggle, { type SegmentedOption } from '../components/common/SegmentedToggle';
import type { HexPlayerComponent, ViewAlertRequest } from '../components/hexagon/types';
import type { HexAlertsApi, MapLayoutApi } from '../api/contracts';

export type BoardView = 'hex' | 'map';

const STORAGE_KEY = 'trinetra.alertboard.view';

// Symbol + name. The icons are the sidebar's own — the hexagon board and Map View carried
// these before the merge, so an operator recognises them from the menu they used to be in.
// The tooltip says what each view IS, which neither the name nor the symbol quite manages.
const VIEW_OPTIONS: Record<BoardView, SegmentedOption<BoardView>> = {
  hex: {
    key: 'hex',
    label: 'Honeycomb View',
    icon: RectangleGroupIcon,
    title: 'Every camera, clustered by site',
  },
  map: {
    key: 'map',
    label: 'Map View',
    icon: MapPinIcon,
    title: 'Cameras pinned on a floor plan',
  },
};

export interface AlertBoardPageProps {
  /** Realtime alerts + camera roster. Backs BOTH boards. */
  alertsApi: HexAlertsApi;
  /** Floor-plan layouts and pin placements. Only the map board reads it. */
  mapApi: MapLayoutApi;
  PlayerComponent: HexPlayerComponent;
  /** Caller holds `map.edit` — shows the map's layout and placement editing affordances. */
  canEdit?: boolean;
  /** Cloud: require a selected node; the boards show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → both boards reload. */
  nodeKey?: string;
  className?: string;
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * This board covers ONE site — the honeycomb draws every group on a single shared
   * background instead of a panel per site. Local passes it (the node IS the site); cloud
   * leaves it off, where each node genuinely is a different place.
   */
  singleSite?: boolean;

  /**
   * Which views this user may see, in toolbar order. Defaults to both.
   *
   * The two boards carry different RBAC keys (`alerts.view` for the honeycomb, `map.view` for
   * the map), and merging the pages must not merge the permissions — a viewer entitled to only
   * one of them gets that one, with no toggle at all rather than a button that bounces them to
   * the dashboard.
   */
  allowedViews?: BoardView[];

  /**
   * The current view. Controlled, because each app keeps it in the URL — that's what makes
   * the merged board deep-linkable, bookmarkable and able to light up the right sidebar item.
   */
  view: BoardView;
  /** Called with the view the operator picked. The app is expected to route to it. */
  onViewChange: (view: BoardView) => void;

  /**
   * Remember the operator's last view across sessions: on mount, a remembered view is
   * re-selected through `onViewChange`.
   *
   * Both sidebars point at the honeycomb's URL, so without this an operator who works from
   * the floor plan would have to toggle to it on every visit. Leave off where the caller pins
   * the view (Multi-Site) — there is nothing to remember.
   */
  rememberView?: boolean;
  /** Scopes what `rememberView` stores. Cloud passes the node id, so each site keeps its own
   *  preference; local has one site and needs no suffix. */
  persistKey?: string;
}

export default function AlertBoardPage({
  alertsApi,
  mapApi,
  PlayerComponent,
  canEdit = false,
  requireNode = false,
  nodeKey,
  className,
  onViewAlert,
  onOpenRecordings,
  allowedViews = ['hex', 'map'],
  singleSite = false,
  view: requested,
  onViewChange,
  rememberView = false,
  persistKey,
}: AlertBoardPageProps) {
  const views = allowedViews.length ? allowedViews : (['hex'] as BoardView[]);
  const storageKey = rememberView ? `${STORAGE_KEY}${persistKey ? `.${persistKey}` : ''}` : null;

  // A view the caller no longer allows (permissions changed, or a deep link into the other
  // board) falls back rather than rendering a board this user must not see. The app wrappers
  // apply the same fallback to the URL, so the address bar doesn't end up naming a board that
  // isn't on screen.
  const view = views.includes(requested) ? requested : views[0];

  const setView = useCallback(
    (next: BoardView) => {
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, next);
        } catch {
          /* private mode — the choice just won't survive a reload */
        }
      }
      onViewChange(next);
    },
    [onViewChange, storageKey],
  );

  // Restore the remembered view once per storage key — so on the way in, and again when the
  // key changes under us (cloud scopes it per node, so switching site restores the view that
  // site was last watched in). Not on every render: after the restore the operator's clicks
  // are the source of truth, and re-running would keep dragging them back.
  const restoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storageKey || restoredForRef.current === storageKey) return;
    restoredForRef.current = storageKey;
    try {
      const saved = localStorage.getItem(storageKey) as BoardView | null;
      if (saved && saved !== view && views.includes(saved)) onViewChange(saved);
    } catch {
      /* private mode — nothing was ever remembered */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Flipping to the honeycomb mid-edit would unmount the map and drop the unsaved pins, so
  // the toggle locks until the operator saves or cancels.
  const [mapEditing, setMapEditing] = useState(false);
  const onEditingChange = useCallback((editing: boolean) => setMapEditing(editing), []);
  // The map is what owns this state; leaving it can't leave the lock behind.
  useEffect(() => {
    if (view !== 'map') setMapEditing(false);
  }, [view]);

  const headerSlot = useMemo(
    () =>
      views.length < 2 ? null : (
        <SegmentedToggle<BoardView>
          aria-label="Board view"
          options={views.map((v) => VIEW_OPTIONS[v])}
          value={view}
          onChange={setView}
          disabled={mapEditing}
          disabledTitle="Save or cancel the layout first"
        />
      ),
    // `views` is a fresh array each render; its CONTENT is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [views.join(','), view, setView, mapEditing],
  );

  if (view === 'map') {
    return (
      <CameraMapPage
        api={mapApi}
        alertsApi={alertsApi}
        PlayerComponent={PlayerComponent}
        canEdit={canEdit}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={className}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        headerSlot={headerSlot}
        onEditingChange={onEditingChange}
      />
    );
  }

  return (
    <AlertHexagonPage
      api={alertsApi}
      PlayerComponent={PlayerComponent}
      requireNode={requireNode}
      nodeKey={nodeKey}
      className={className}
      onViewAlert={onViewAlert}
      onOpenRecordings={onOpenRecordings}
      headerSlot={headerSlot}
      singleSite={singleSite}
    />
  );
}
